import React, { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionsContainer,
  Title,
  TitleContainer,
  Action,
  BookmarkAction,
  tooltipStyles,
  TitleContentWrapper,
  BackButton,
  TitleWrapper,
  StyledLogoLink
} from './styled';
import { Icon } from '../../../Icon';
import LogoIcon from '../../../../public/static/images/item/small-logo.svg';
import { useStickyHeader } from '../../../../hooks/useStickyHeader';
import { ReportSection } from '../ReportSection';
import { Bookmark } from '../../../Bookmark';
import { Tooltip } from '../../../ui/Tooltip';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';
import { useBGBannerPadding } from '../../../../hooks/useBGBannerPadding';
import { PlaceholderBlock } from '../../../../styled/placeholders.styled';
import { selectPromotionModalViewState } from '../../../../store/selectors';

interface CurrentItemTitleProps {
  isReportButtonShown: boolean;
  isBookmarkedButtonShow: boolean;
  title: ReactNode;
  isReported: boolean;
  itemId: string;
  isFavorited: boolean;
  isPhotosGrid?: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAdReported: React.Dispatch<React.SetStateAction<boolean>>;
  handleBack?: () => void;
  hideOnMobile?: boolean;
  userRelatedItemLoading?: boolean;
}

export const CurrentItemTitle: React.FC<CurrentItemTitleProps> = ({
  isReportButtonShown,
  isBookmarkedButtonShow,
  title,
  isReported,
  itemId,
  isFavorited,
  isPhotosGrid = false,
  setFavorited,
  setIsAdReported,
  handleBack,
  hideOnMobile = false,
  userRelatedItemLoading
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const { isSmall } = useScreenSize();
  const isClient = useClient();
  const { isVisible: isVisiblePromotionModalView } = useSelector(
    selectPromotionModalViewState
  );

  const { isFixed, headerHeight } = useStickyHeader({
    isAdPage: true,
    containerRef
  });
  useBGBannerPadding(
    headerHeight,
    false,
    isReportModalOpen || isVisiblePromotionModalView,
    isFixed
  );

  const bookmark = (
    <BookmarkAction>
      <Bookmark
        itemId={itemId}
        isActive={isFavorited}
        setFavorited={setFavorited}
        renderCurrentIcons
        shouldUpdateCache
      />
    </BookmarkAction>
  );
  const showBookmarks =
    isSmall && isClient && isPhotosGrid && isBookmarkedButtonShow;
  const isTitleFixed = isFixed && !isPhotosGrid;

  return (
    <TitleWrapper $hideOnMobile={hideOnMobile}>
      {isTitleFixed && <div style={{ height: headerHeight }} />}
      <Title
        ref={containerRef}
        $isFixed={isTitleFixed}
        $isPhotosGrid={isPhotosGrid}
        data-cy="current-item-title"
      >
        <TitleContentWrapper>
          {isPhotosGrid && handleBack && (
            <BackButton data-cy="all-photos-back-button" onClick={handleBack} />
          )}

          <TitleContainer>
            <StyledLogoLink href="/" aria-label="Logo" $visible={isTitleFixed}>
              <Icon
                indent={false}
                IconComponent={LogoIcon}
                width={30}
                height={30}
              />
            </StyledLogoLink>

            {title}
          </TitleContainer>

          {showBookmarks && bookmark}

          {!isSmall && isClient && userRelatedItemLoading && (
            <PlaceholderBlock
              width="123px"
              height="54px"
              margin="0"
              borderRadius="14px"
            />
          )}

          {!isSmall && isClient && !userRelatedItemLoading && (
            <ActionsContainer>
              {isBookmarkedButtonShow && (
                <Tooltip
                  text={t('pages.bookmarks.title')}
                  styles={tooltipStyles}
                  tooltipTopSpace={60}
                >
                  {bookmark}
                </Tooltip>
              )}
              {isReportButtonShown && (
                <Tooltip
                  text={t(
                    isReported
                      ? 'pages.current_item.buttons.sent_complain'
                      : 'pages.current_item.buttons.complain'
                  )}
                  styles={tooltipStyles}
                  tooltipTopSpace={60}
                  disabled={isReportModalOpen}
                >
                  <Action>
                    <ReportSection
                      itemId={itemId}
                      isReported={isReported}
                      setIsReportModalOpen={setIsReportModalOpen}
                      setAdIsReported={setIsAdReported}
                    />
                  </Action>
                </Tooltip>
              )}
            </ActionsContainer>
          )}
        </TitleContentWrapper>
      </Title>
    </TitleWrapper>
  );
};
